import React from "react";
import { PRICES, lowestPrice, usd } from "../data/pricing";

const SITE_URL = "https://www.emissionsonthemove.com";
const SOCIAL_IMAGE = `${SITE_URL}/eotm_with_car_and_employees.jpeg`;

export const routeMetadata = {
  "/": {
    title: "Mobile Emissions Testing & On-The-Spot DMV Renewals | Emissions on the Move",
    description:
      "Skip the lines. Certified mobile emissions testing, on-the-spot DMV registration renewals, and VIN inspections at your home or work in Utah County. Book online or call (385) 535-4917.",
  },
  "/services/": {
    title: "Mobile Emissions Testing, DMV Renewals & VIN Inspections | Emissions on the Move",
    description:
      "Explore our Utah County mobile automotive services: state-certified emissions testing, authorized On-The-Spot DMV registration renewals, Form TC-661 VIN inspections, and business fleet testing.",
  },
  "/pricing/": {
    title: `Emissions Testing Prices from ${usd(lowestPrice)} | Emissions on the Move`,
    description: `Affordable mobile emissions testing in Utah County. Flexible 2-hour window for ${usd(
      PRICES.flexible
    )}, exact appointment time for ${usd(PRICES.exact)}, additional vehicles ${usd(
      PRICES.additionalVehicle
    )} each. No hidden fees.`,
  },
  "/faq/": {
    title: "Emissions Testing & On-The-Spot Renewal FAQ | Emissions on the Move",
    description:
      "Answers about mobile emissions testing, On-The-Spot registration renewals, VIN inspections, free retests, and fleet service in Utah County.",
  },
  "/book-now/": {
    title: "Book Mobile Emissions Testing in Utah County | Emissions on the Move",
    description:
      "Book your mobile emissions test online. Choose an exact appointment time or a flexible 2-hour window and we'll come to you anywhere in Utah County.",
  },
  "/contact/": {
    title: "Contact Us | Emissions on the Move",
    description:
      "Contact Emissions on the Move for mobile emissions testing in Utah County. Call or text (385) 535-4917, email andersonemissions@gmail.com, or send us a message.",
  },
  "/booking-confirmation/": {
    title: "Booking Confirmed | Emissions on the Move",
    description:
      "Your mobile emissions inspection appointment is confirmed! We look forward to testing your vehicle in Utah County.",
  },
};

const normalizePath = (path) => {
  if (!path || path === "/") return "/";
  return `${path.replace(/\/+$/, "")}/`;
};

const businessSchema = {
  "@type": "AutomotiveBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "Emissions on the Move",
  description:
    "Mobile vehicle emissions testing, authorized On-The-Spot DMV registration renewals, and VIN inspections serving all of Utah County, Utah. We come to your home or work.",
  url: `${SITE_URL}/`,
  telephone: "+1-385-535-4917",
  email: "andersonemissions@gmail.com",
  image: SOCIAL_IMAGE,
  logo: `${SITE_URL}/logo.png`,
  priceRange: "$$",
  areaServed: {
    "@type": "AdministrativeArea",
    name: "Utah County, Utah",
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Provo",
    addressRegion: "UT",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 40.2338,
    longitude: -111.6585,
  },
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: {
      "@type": "GeoCoordinates",
      latitude: 40.2338,
      longitude: -111.6585,
    },
    geoRadius: 40000,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "18:00",
    },
  ],
  sameAs: [
    "https://instagram.com/emissionsonthemove",
    "https://www.facebook.com/profile.php?id=61590274666347",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Mobile emissions testing appointments",
    itemListElement: [
      {
        "@type": "Offer",
        name: "Flexible Window Mobile Emissions Test",
        priceCurrency: "USD",
        price: String(PRICES.flexible),
      },
      {
        "@type": "Offer",
        name: "Exact Time Mobile Emissions Test",
        priceCurrency: "USD",
        price: String(PRICES.exact),
      },
    ],
  },
};

const SEO = ({ path, structuredData }) => {
  const canonicalPath = normalizePath(path);
  const metadata = routeMetadata[canonicalPath] || routeMetadata["/"];
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const graph = [
    businessSchema,
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Emissions on the Move",
      publisher: { "@id": `${SITE_URL}/#business` },
      inLanguage: "en-US",
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: metadata.title,
      description: metadata.description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#business` },
      inLanguage: "en-US",
    },
    ...(structuredData ? [structuredData] : []),
  ];

  return (
    <>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Emissions on the Move" />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={SOCIAL_IMAGE} />
      <meta property="og:image:width" content="2048" />
      <meta property="og:image:height" content="1536" />
      <meta property="og:image:alt" content="Emissions on the Move mobile testing team and service vehicle" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
      <meta name="twitter:image" content={SOCIAL_IMAGE} />
      <meta name="twitter:image:alt" content="Emissions on the Move mobile testing team and service vehicle" />

      <script type="application/ld+json">
        {JSON.stringify({ "@context": "https://schema.org", "@graph": graph })}
      </script>
    </>
  );
};

export default SEO;
